import React, { useMemo, useRef } from 'react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { useFragment, graphql } from 'react-relay';
import Badge from '@atlaskit/badge';
import { Text } from '@atlaskit/primitives/compiled';
import { useIntl } from '@atlassian/jira-intl';
import { useBespokeEmptyFieldTextExperiment } from '@atlassian/jira-issue-experiments/src/bespoke-empty-field-text-experiment/index.tsx';
import { EmptyFieldText } from '@atlassian/jira-issue-empty-field-text/src/ui/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { HoverPopover } from '@atlassian/jira-hover-popover/src/ui/index.tsx';
import { mergeRefs } from '@atlassian/jira-merge-refs/src/index.tsx';
import { useHoverPopoverContentRef } from '@atlassian/jira-hover-popover/src/controllers/hover-popover-content-provider/index.tsx';
import type { originalEstimate_issueFieldOriginalEstimateReadviewFull_OriginalEstimateReadView$key as OriginalEstimateFragment } from '@atlassian/jira-relay/src/__generated__/originalEstimate_issueFieldOriginalEstimateReadviewFull_OriginalEstimateReadView.graphql';
import type { OriginalEstimateReadViewProps } from './types.tsx';
import { useFormatInputValue } from './utils/formatting.tsx';
import messages from './messages.tsx';

/**
 * The OriginalEstimateReadView will show a read only view of OriginalEstimate field.
 * @param props [OriginalEstimateReadViewProps](./types.tsx)
 */
export const OriginalEstimateReadView = ({
	fragmentRef,
	shouldRenderHoverPopover,
	issueFieldId,
}: Omit<OriginalEstimateReadViewProps, 'fragmentRef'> & {
	fragmentRef: OriginalEstimateFragment | null;
}) => {
	const data = useFragment<OriginalEstimateFragment>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment originalEstimate_issueFieldOriginalEstimateReadviewFull_OriginalEstimateReadView on JiraTimeTrackingField {
				originalEstimate {
					timeInSeconds
				}
				timeTrackingSettings {
					workingHoursPerDay
					workingDaysPerWeek
					defaultFormat
					defaultUnit
				}
			}
		`,
		fragmentRef,
	);
	const { formatMessage } = useIntl();
	const noValueTextExperiment = formatMessage(messages.noValueTextExperiment);
	const { isExperimentEnabled } = useBespokeEmptyFieldTextExperiment();

	const settingsInput = {
		workingHoursPerDay: data?.timeTrackingSettings?.workingHoursPerDay,
		workingDaysPerWeek: data?.timeTrackingSettings?.workingDaysPerWeek,
		defaultFormat: data?.timeTrackingSettings?.defaultFormat,
		defaultUnit: data?.timeTrackingSettings?.defaultUnit,
	};

	const formatInputValue = useFormatInputValue(settingsInput);
	const timeString = useMemo(
		() => (data ? formatInputValue(data.originalEstimate?.timeInSeconds ?? 0) : null),
		[data, formatInputValue],
	);
	const contentRef = useRef(null);
	const hoverPopoverContentRefFactory =
		shouldRenderHoverPopover && fg('issue_table_single_line_row_height_event_delegator')
			? // eslint-disable-next-line react-hooks/react-compiler, react-hooks/rules-of-hooks
				useHoverPopoverContentRef(issueFieldId)
			: null;

	let hoverPopoverContentRef = null;

	if (hoverPopoverContentRefFactory && fg('issue_table_single_line_row_height_event_delegator')) {
		hoverPopoverContentRef = hoverPopoverContentRefFactory({
			content: timeString,
		});
	}

	if (!data?.originalEstimate?.timeInSeconds && isExperimentEnabled) {
		return <EmptyFieldText emptyText={noValueTextExperiment} />;
	}

	if (timeString === null) {
		return <EmptyFieldText />;
	}

	if (shouldRenderHoverPopover) {
		return fg('issue_table_single_line_row_height_event_delegator') ? (
			// Empty div to allow ellipsis
			// eslint-disable-next-line jira/react/no-empty-divs
			<div>
				<Badge>
					<Text maxLines={1} ref={hoverPopoverContentRef}>
						{timeString}
					</Text>
				</Badge>
			</div>
		) : (
			<HoverPopover contentRef={contentRef} content={timeString}>
				{({ ref }) => (
					// Empty div to allow ellipsis
					// eslint-disable-next-line jira/react/no-empty-divs
					<div>
						<Badge>
							<Text maxLines={1} ref={mergeRefs(ref, contentRef)}>
								{timeString}
							</Text>
						</Badge>
					</div>
				)}
			</HoverPopover>
		);
	}

	return <Badge>{timeString}</Badge>;
};
